import React, { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageLoader } from "@/components/common/Loading";
import { ScopeTag } from "@/components/common/SourceSelect";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle, ArrowDown, ArrowUp, ArrowUpDown, Check, EyeOff, GripVertical, Pencil, Plus, Radio, Trash2, X } from "lucide-react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function detailMsg(e) {
  const d = e?.response?.data?.detail;
  return d && typeof d === "object" ? d : null;
}

function SourceRow({ s, orgId, canHideHere, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(s.label);
  const save = async (confirm = false) => {
    try { await api.patch(`/sources/${s.id}`, { label, confirm_separate: confirm }); toast.success("Source renamed"); setEditing(false); onChanged(); }
    catch (e) {
      const d = detailMsg(e);
      if (d?.code === "inherited_duplicate" && window.confirm(`${d.message}\n\nKeep as a separate source?`)) return save(true);
      toast.error(d?.message || apiError(e));
    }
  };
  const toggle = async () => { try { await api.patch(`/sources/${s.id}`, { active: !s.active }); onChanged(); } catch (e) { toast.error(apiError(e)); } };
  const hide = async (hidden) => { try { await api.post(`/sources/${s.id}/visibility`, { organization_id: orgId, hidden }); onChanged(); } catch (e) { toast.error(apiError(e)); } };
  const remove = async () => {
    if (!window.confirm(`Delete “${s.label}”? Only unused sources can be deleted.`)) return;
    try { await api.delete(`/sources/${s.id}`); toast.success("Deleted"); onChanged(); } catch (e) { toast.error(apiError(e)); }
  };
  const inherited = s.scope !== "group";
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-100 px-3 py-2" data-testid={`source-item-${s.id}`}>
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {editing ? (
          <>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} className="h-8" data-testid={`source-rename-input-${s.id}`} onKeyDown={(e) => e.key === "Enter" && save()} />
            <Button size="sm" variant="outline" onClick={() => save()} data-testid={`source-rename-save-${s.id}`}><Check className="w-3.5 h-3.5" /></Button>
            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setLabel(s.label); }}><X className="w-3.5 h-3.5" /></Button>
          </>
        ) : (
          <>
            <span className={`text-sm truncate ${s.active && !s.hidden_here ? "text-slate-700" : "text-slate-400 line-through"}`}>{s.label}</span>
            <ScopeTag s={s} />
            {!s.active && <span className="text-[10px] text-slate-400">inactive</span>}
            {s.hidden_here && <span className="text-[10px] text-amber-700">hidden for this group</span>}
            <span className="text-[10px] text-slate-400">{s.usage ? `${s.usage} record${s.usage > 1 ? "s" : ""}` : "unused"}</span>
          </>
        )}
      </div>
      {!editing && (
        <div className="flex items-center gap-1.5">
          {s.can_edit && <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid={`source-rename-${s.id}`} aria-label="Rename"><Pencil className="w-3.5 h-3.5" /></Button>}
          {s.can_edit && !s.usage && <Button size="sm" variant="ghost" onClick={remove} className="text-rose-600" data-testid={`source-delete-${s.id}`} aria-label="Delete"><Trash2 className="w-3.5 h-3.5" /></Button>}
          {inherited && canHideHere && (
            <Button size="sm" variant="ghost" onClick={() => hide(!s.hidden_here)} data-testid={`source-hide-${s.id}`} title="Hide or show only for the selected group">
              <EyeOff className="w-3.5 h-3.5 mr-1" />{s.hidden_here ? "Show here" : "Hide here"}</Button>
          )}
          {s.can_edit && <Switch checked={!!s.active} onCheckedChange={toggle} data-testid={`source-toggle-${s.id}`} />}
        </div>
      )}
    </li>
  );
}

function AddSource({ data, onAdded }) {
  const scopes = [...(data.can_manage_system ? [["system", "System"]] : []), ...(data.can_manage_account ? [["account", "Account (all groups)"]] : []),
    ...(data.group_admin_orgs.length ? [["group", "Group"]] : [])];
  const [scope, setScope] = useState(scopes.find((x) => x[0] === "account")?.[0] || scopes[0]?.[0] || "");
  const [org, setOrg] = useState(data.group_admin_orgs[0]?.id || "");
  const [label, setLabel] = useState("");
  const add = async (confirm = false) => {
    if (!label.trim()) return;
    try {
      await api.post("/sources", { label: label.trim(), scope, organization_id: scope === "group" ? org : null, confirm_separate: confirm });
      setLabel(""); toast.success("Source added"); onAdded();
    } catch (e) {
      const d = detailMsg(e);
      if (d?.code === "inherited_duplicate" && window.confirm(`${d.message}\n\nAdd it anyway as a separate source?`)) return add(true);
      toast.error(d?.message || apiError(e));
    }
  };
  if (!scopes.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-4">
      <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Job Fair" onKeyDown={(e) => e.key === "Enter" && add()} className="flex-1 min-w-[160px]" data-testid="source-input" />
      <Select value={scope} onValueChange={setScope}>
        <SelectTrigger className="w-[170px]" data-testid="source-scope"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white">{scopes.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
      </Select>
      {scope === "group" && (
        <Select value={org} onValueChange={setOrg}>
          <SelectTrigger className="w-[200px]" data-testid="source-group"><SelectValue placeholder="Group" /></SelectTrigger>
          <SelectContent className="bg-white">{data.group_admin_orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
        </Select>
      )}
      <Button onClick={() => add()} className="bg-brand hover:bg-brand-strong" data-testid="source-add"><Plus className="w-4 h-4" /></Button>
    </div>
  );
}

function SortableSourceRow({ s, i, last, move }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: s.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <li ref={setNodeRef} style={style}
      className={`flex items-center gap-2 rounded-md border bg-white px-2 py-1.5 ${isDragging ? "relative z-10 border-brand shadow-lg opacity-90" : "border-slate-200"}`}
      data-testid={`source-reorder-item-${s.id}`}>
      <button type="button" ref={setActivatorNodeRef} {...attributes} {...listeners} aria-label={`Drag ${s.label}`}
        className="touch-none cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 p-1 rounded focus:outline-none focus:ring-2 focus:ring-brand/40"
        data-testid={`source-drag-handle-${s.id}`}><GripVertical className="w-4 h-4" /></button>
      <span className="w-6 text-xs text-slate-400 text-right">{i + 1}</span>
      <span className="text-sm text-slate-700 flex-1 min-w-0 truncate">{s.label}</span>
      <ScopeTag s={s} />
      <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up" data-testid={`source-move-up-${s.id}`}><ArrowUp className="w-3.5 h-3.5" /></Button>
      <Button size="sm" variant="ghost" disabled={last} onClick={() => move(i, 1)} aria-label="Move down" data-testid={`source-move-down-${s.id}`}><ArrowDown className="w-3.5 h-3.5" /></Button>
    </li>
  );
}

function ReorderList({ items, onDone }) {
  const [rows, setRows] = useState(() => [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
  const [saving, setSaving] = useState(false);
  const move = (i, d) => setRows((r) => arrayMove(r, i, i + d));
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setRows((r) => arrayMove(r, r.findIndex((s) => s.id === active.id), r.findIndex((s) => s.id === over.id)));
  };
  const save = async () => {
    setSaving(true);
    try { await api.post("/sources/reorder", { ids: rows.map((s) => s.id) }); toast.success("Source order saved"); onDone(true); }
    catch (e) { toast.error(apiError(e)); setSaving(false); }
  };
  return (
    <div className="mb-4 rounded-md border border-brand/30 bg-brand/5 p-3" data-testid="source-reorder-panel">
      <p className="text-xs text-slate-600 mb-2">This order is used in every Source dropdown. Drag sources by the handle (or use the arrows), then save.</p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={rows.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <ol className="space-y-1">
            {rows.map((s, i) => <SortableSourceRow key={s.id} s={s} i={i} last={i === rows.length - 1} move={move} />)}
          </ol>
        </SortableContext>
      </DndContext>
      <div className="flex justify-end gap-2 mt-3">
        <Button size="sm" variant="outline" onClick={() => onDone(false)} data-testid="source-reorder-cancel">Cancel</Button>
        <Button size="sm" onClick={save} disabled={saving} className="bg-brand hover:bg-brand-strong" data-testid="source-reorder-save">Save order</Button>
      </div>
    </div>
  );
}

export function SourcesManager() {
  const { isSuper } = useAuth();
  const [reordering, setReordering] = useState(false);
  const [orgId, setOrgId] = useState("");
  const [data, setData] = useState(null);
  const [linkInfo, setLinkInfo] = useState(null);
  const load = useCallback(() => {
    api.get("/sources", { params: { manage: true, organization_id: orgId || undefined } }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [orgId]);
  useEffect(() => { load(); }, [load]);
  const link = async (apply) => {
    try { const { data: d } = await api.post("/sources/link-legacy", null, { params: { apply } }); setLinkInfo(d); if (apply) { toast.success(`Linked ${d.linked} records`); load(); } }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!data) return <PageLoader />;
  const canHideHere = !!orgId && data.group_admin_orgs.some((o) => o.id === orgId);
  const groups = [["system", "System"], ["account", "Account"], ["group", "Group"]];
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="source-config">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-brand/10 flex items-center justify-center"><Radio className="w-4 h-4 text-brand" /></span>
          <h3 className="font-display font-semibold text-slate-900 text-sm">Candidate Sources</h3>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        {isSuper && !reordering && data.items.length > 1 && (
          <Button size="sm" variant="outline" onClick={() => setReordering(true)} data-testid="source-reorder-button"><ArrowUpDown className="w-3.5 h-3.5 mr-1" />Reorder</Button>
        )}
        <Select value={orgId || "all"} onValueChange={(v) => setOrgId(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[220px] h-9" data-testid="source-org-view"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All sources I can see</SelectItem>
            {data.group_admin_orgs.map((o) => <SelectItem key={o.id} value={o.id}>As seen by {o.name}</SelectItem>)}
          </SelectContent>
        </Select>
        </div>
      </div>
      {reordering && <ReorderList items={data.items} onDone={(saved) => { setReordering(false); if (saved) load(); }} />}
      <p className="text-xs text-slate-500 mb-3">System and Account sources are inherited by every group (including new ones). Group sources are only available to their group. Deactivated sources stay on existing records.</p>
      <AddSource data={data} onAdded={load} />
      {groups.map(([k, title]) => {
        const rows = data.items.filter((s) => s.scope === k).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        if (!rows.length) return null;
        return (
          <div key={k} className="mb-4" data-testid={`source-group-${k}`}>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">{title} sources</p>
            <ul className="space-y-1.5">{rows.map((s) => <SourceRow key={s.id} s={s} orgId={orgId} canHideHere={canHideHere} onChanged={load} />)}</ul>
          </div>
        );
      })}
      {data.review?.potential_duplicates?.length > 0 && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="source-duplicates">
          <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />Possible duplicates to review (never merged automatically)</p>
          <ul className="mt-1 text-xs text-amber-800 space-y-0.5">
            {data.review.potential_duplicates.map((d, i) => <li key={i}>{d.a.label} ({d.a.owner_name}) · {d.b.label} ({d.b.owner_name}){d.kind === "same_name" ? " — same name" : " — similar"}</li>)}
          </ul>
        </div>
      )}
      {data.review?.unlinked_labels?.length > 0 && (
        <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="source-legacy">
          <p className="text-sm font-semibold text-slate-700">Older records storing only a source name</p>
          <ul className="mt-1 text-xs text-slate-600 space-y-0.5 max-h-40 overflow-y-auto">
            {data.review.unlinked_labels.map((l) => <li key={l.label}>{l.label}: {l.records} record{l.records > 1 ? "s" : ""}{l.matches === 0 ? " — no matching source (review)" : ""}</li>)}
          </ul>
          {data.can_manage_account && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => link(false)} data-testid="source-link-preview">Preview linking</Button>
              {linkInfo && !linkInfo.applied && <Button size="sm" onClick={() => link(true)} className="bg-brand hover:bg-brand-strong" data-testid="source-link-apply">Link {linkInfo.linked} records</Button>}
              {linkInfo && <span className="text-xs text-slate-500">{linkInfo.linked} matchable · {Object.keys(linkInfo.ambiguous).length} ambiguous · {Object.keys(linkInfo.unmatched).length} unmatched names</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
